import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { PlusIcon, PencilIcon, TrashIcon, LoaderIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { UserName } from '@/components/UserName';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { formatTimestamp } from '@/utils/utils';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { BoxIcon, EmptyBoxIcon, ErrorCircleIcon } from './shared';

export default function SandboxClasses() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [classDeleteUid, setClassDeleteUid] = useState<string | null>(null);

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1);
  }, [searchKeyword]);

  const {
    data: classesData,
    isLoading: classesLoading,
    error: classesError,
    refetch: classesRefetch,
  } = useQuery({
    queryKey: ['sandbox-classes', page, pageSize, searchKeyword],
    queryFn: () =>
      sandboxClassApi.listSandboxClasses(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: searchKeyword.trim() ? { name: searchKeyword.trim() } : {},
        })
      ),
  });

  const classDeleteMutation = useMutation({
    mutationFn: (uid: string) => sandboxClassApi.deleteSandboxClass(uid),
    onSuccess: () => {
      setClassDeleteUid(null);
      queryClient.invalidateQueries({ queryKey: ['sandbox-classes'] });
    },
    onError: (error) => toast.error(`删除失败：${fullErrorText(error)}`),
  });

  const classes = (classesData?.data || []).filter(Boolean);
  const keyword = searchKeyword.trim();
  const totalPages = Math.max(1, Math.ceil((classesData?.total || 0) / pageSize));

  return (
    <>
      <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BoxIcon />
            <span className="text-sm font-medium text-foreground">沙箱模版</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                placeholder="搜索名称..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="pl-9 h-9 w-48 bg-muted/50 border-0 focus:bg-background"
              />
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/plugins/sandboxes/create-class')}
              className="gap-1.5"
            >
              <PlusIcon className="h-4 w-4" />
              新建
            </Button>
          </div>
        </div>

        {classesLoading ? (
          <div className="divide-y">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <div className="ml-auto flex gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-8" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        ) : classesError ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <span className="text-destructive">
                <ErrorCircleIcon />
              </span>
            </div>
            <p className="font-semibold text-foreground mb-1">加载失败</p>
            <p className="text-sm text-muted-foreground mb-5">请检查网络连接后重试</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => classesRefetch()}
              className="gap-1.5"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              重试
            </Button>
          </div>
        ) : classes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <EmptyBoxIcon />
            <p className="font-semibold text-foreground mt-4 mb-1">
              {keyword ? '没有找到匹配的沙箱模版' : '暂无沙箱模版'}
            </p>
            <p className="text-sm text-muted-foreground mb-5">
              {keyword ? '尝试其他关键词搜索' : '创建第一个沙箱模版来开始使用'}
            </p>
            {!keyword && (
              <Button
                size="sm"
                onClick={() => navigate('/plugins/sandboxes/create-class')}
                className="gap-1.5"
              >
                <PlusIcon className="h-4 w-4" />
                新建沙箱模版
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-11">名称</TableHead>
                <TableHead className="h-11">描述</TableHead>
                <TableHead className="h-11">创建者</TableHead>
                <TableHead className="h-11 w-28">创建时间</TableHead>
                <TableHead className="h-11 w-28">更新时间</TableHead>
                <TableHead className="h-11 w-48">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {classes.map((item) => (
                <TableRow
                  key={item.id}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/plugins/sandboxes/classes/${item.id}`)}
                >
                  <TableCell className="font-medium py-4">
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="text-primary hover:text-primary/80 font-medium text-left truncate block"
                        onClick={() => navigate(`/plugins/sandboxes/classes/${item.id}`)}
                      >
                        {item.name}
                      </button>
                      <span className="text-xs text-muted-foreground/60">ID: {item.id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3">
                    <span className="text-muted-foreground text-sm truncate max-w-[200px] inline-block">
                      {item.description || (
                        <span className="italic text-muted-foreground/50">暂无描述</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="py-3">
                    <span className="text-sm text-muted-foreground">
                      <UserName userId={item.userId} />
                    </span>
                  </TableCell>
                  <TableCell className="py-3 text-muted-foreground text-sm">
                    {formatTimestamp(item.createdAt)}
                  </TableCell>
                  <TableCell className="py-3 text-muted-foreground text-sm">
                    {formatTimestamp(item.updatedAt)}
                  </TableCell>
                  <TableCell className="py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() =>
                          navigate(`/plugins/sandboxes/create-instance-from-class/${item.id}`)
                        }
                      >
                        <PlusIcon className="h-3 w-3" />
                        创建实例
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => navigate(`/plugins/sandboxes/classes/${item.id}/edit`)}
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => setClassDeleteUid(item.id)}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!classesLoading && !classesError && classes.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/10 gap-4">
            <p className="text-sm text-muted-foreground">
              共 <span className="font-medium text-foreground">{classesData?.total || 0}</span> 条
              {keyword && <span className="ml-1">匹配结果</span>}
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">每页</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-muted-foreground">条</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-background border text-xs">
                  <span className="font-medium">{page}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground">{totalPages}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={!!classDeleteUid} onOpenChange={() => setClassDeleteUid(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除该沙箱模版吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setClassDeleteUid(null)}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (classDeleteUid) classDeleteMutation.mutate(classDeleteUid);
              }}
              disabled={classDeleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {classDeleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
